// src/components/store/boost/BoostPackageSelection.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiArrowLeft, 
  FiTrendingUp, 
  FiEye, 
  FiList, 
  FiArrowRight,
  FiRefreshCw,
  FiPlusCircle,
  FiCheck
} from 'react-icons/fi';
import Badge from '../../../components/common/Badge';
import Button from '../../../components/common/Button';
import Loading from '../../../components/common/Loading';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import { useAuth } from '../../../context/AuthContext';
import { useSellerData } from '../../../hooks/useSellerData';
import { IMAGE_BASE_URL } from '../../../services/api';

const BoostPackageSelection = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { listings, loading: listingsLoading, refreshListings } = useSellerData();
  
  const [selectedListing, setSelectedListing] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Filter only active listings that can be boosted
  const boostableListings = listings.filter(listing => 
    listing.status === 'active' && !listing.has_active_boost
  );

  const onRefresh = async () => {
    setRefreshing(true);
    await refreshListings();
    setRefreshing(false);
  };

  const handleContinue = () => {
    if (!selectedListing) {
      alert('Please select a listing to boost first.');
      return;
    }
    
    navigate(`/store/boost-package/${selectedListing.listing_id}`);
  };

  const handleCreateListing = () => {
    if (window.confirm('You need an active listing to use the boost feature. Would you like to create a new listing?')) {
      navigate('/store/listings/create-listing');
    }
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) {
      return image.replace(/\\\//g, '/');
    }
    return `${IMAGE_BASE_URL}${image}`;
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (listingsLoading && !refreshing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loading size="large" text="Loading your listings..." />
      </div>
    );
  }

  const hasBoostedListings = listings.filter(l => l.has_active_boost).length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header />
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <FiArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Boost Your Listings
                </h1>
                <p className="text-sm text-gray-500">
                  Select a listing to boost for more visibility
                </p>
              </div>
            </div>
            <button
              onClick={onRefresh}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <FiRefreshCw className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Introduction */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
          <div className="p-8 text-center">
            <div className="w-20 h-20 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
              <FiTrendingUp className="w-10 h-10 text-orange-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Boost Your Sales 🚀
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Select a listing to boost and get more visibility, clicks, and sales. 
              Choose from our boost packages below.
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Listings Column */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Select a Listing to Boost
                  </h3>
                  <span className="text-sm text-gray-500">
                    {boostableListings.length} available
                  </span>
                </div>
              </div>

              <div className="p-6">
                {boostableListings.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                      <FiList className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No Listings Available
                    </h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                      {listings.length === 0 
                        ? "You don't have any active listings to boost."
                        : "All your active listings are already boosted or not available for boosting."
                      }
                    </p>
                    <Button
                      onClick={handleCreateListing}
                      variant="primary"
                      icon={<FiPlusCircle className="w-5 h-5" />}
                      iconPosition="left"
                    >
                      {listings.length === 0 ? 'Create First Listing' : 'Create New Listing'}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {boostableListings.map((listing) => {
                      const imageUrl = listing.images?.[0]?.image_url 
                        ? getImageUrl(listing.images[0].image_url)
                        : null;

                      return (
                        <div
                          key={listing.listing_id}
                          className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                            selectedListing?.listing_id === listing.listing_id
                              ? 'border-orange-500 bg-orange-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setSelectedListing(listing)}
                        >
                          <div className="flex items-start gap-4">
                            {/* Image */}
                            <div className="flex-shrink-0">
                              <img
                                src={imageUrl || '/images/placeholder.jpg'}
                                alt={listing.title}
                                className="w-16 h-16 rounded-lg object-cover"
                                onError={(e) => {
                                  e.target.src = '/images/placeholder.jpg';
                                }}
                              />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-1">
                                <h4 className="font-medium text-gray-900 truncate">
                                  {listing.title}
                                </h4>
                                <div className="flex-shrink-0 ml-2">
                                  {selectedListing?.listing_id === listing.listing_id ? (
                                    <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center">
                                      <FiCheck className="w-3 h-3 text-white" />
                                    </div>
                                  ) : (
                                    <div className="w-5 h-5 rounded-full border-2 border-gray-300"></div>
                                  )}
                                </div>
                              </div>
                              
                              <p className="text-lg font-bold text-orange-600 mb-2">
                                ₦{parseFloat(listing.price || 0).toLocaleString()}
                              </p>
                              
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <div className="flex items-center gap-1">
                                  <FiEye className="w-3 h-3" />
                                  <span>{listing.view_count || 0} views</span>
                                </div>
                                {listing.category_name && (
                                  <span className="font-medium text-gray-900">
                                  {listing.category_name || 'N/A'}
                                </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Already Boosted Section */}
            {hasBoostedListings && (
              <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-green-900 mb-1">
                      Already Boosted
                    </h3>
                    <p className="text-green-800">
                      {listings.filter(l => l.has_active_boost).length} of your listings are currently boosted
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate('/store/boost-history')}
                    variant="outline"
                    className="border-green-300 text-green-700 hover:bg-green-100"
                    icon={<FiArrowRight className="w-4 h-4" />}
                    iconPosition="right"
                  >
                    View Boosted Listings
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Side Panel */}
          <div className="space-y-6">
            {/* Selection Summary */}
            {selectedListing && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Selected Listing
                </h3>
                <div className="flex items-start gap-4 mb-6">
                  <img
                    src={getImageUrl(selectedListing.images?.[0]?.image_url) || '/images/placeholder.jpg'}
                    alt={selectedListing.title}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate mb-1">
                      {selectedListing.title}
                    </h4>
                    <p className="text-lg font-bold text-orange-600">
                      ₦{parseFloat(selectedListing.price || 0).toLocaleString()}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Current Views</span>
                    <span className="font-medium text-gray-900">
                      {selectedListing.view_count || 0}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Category</span>
                    <span className="font-medium text-gray-900">
                      {selectedListing.category_name || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Status</span>
                    <Badge type="status" status={selectedListing.status} />
                  </div>
                </div>

                <Button
                  onClick={handleContinue}
                  variant="primary"
                  fullWidth
                  icon={<FiArrowRight className="w-5 h-5" />}
                  iconPosition="right"
                  className="justify-center"
                >
                  Continue to Boost Packages
                </Button>
              </div>
            )}

            {/* Boost Benefits */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-4">
                Boost Benefits
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FiEye className="w-3 h-3 text-blue-600" />
                  </div>
                  <span className="text-sm text-blue-800">
                    5-10x more views and impressions
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FiTrendingUp className="w-3 h-3 text-blue-600" />
                  </div>
                  <span className="text-sm text-blue-800">
                    Featured placement in search results
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FiCheck className="w-3 h-3 text-blue-600" />
                  </div>
                  <span className="text-sm text-blue-800">
                    Priority customer support
                  </span>
                </li>
              </ul>
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Listing Stats
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Listings</span>
                  <span className="font-semibold text-gray-900">
                    {listings.length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Available to Boost</span>
                  <span className="font-semibold text-gray-900">
                    {boostableListings.length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Already Boosted</span>
                  <span className="font-semibold text-gray-900">
                    {listings.filter(l => l.has_active_boost).length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Active Listings</span>
                  <span className="font-semibold text-gray-900">
                    {listings.filter(l => l.status === 'active').length}
                  </span>
                </div>
              </div>
            </div>

            {/* Need Help */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Need Help?
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Not sure which listing to boost? Boost your newest or best-performing listing first.
              </p>
              <Button
                onClick={() => navigate('/store/boost-package/boost-guide')}
                variant="outline"
                fullWidth
                size="small"
              >
                View Boost Guide
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Footer (for mobile) */}
      {boostableListings.length > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
          <Button
            onClick={handleContinue}
            variant="primary"
            fullWidth
            disabled={!selectedListing}
            icon={<FiArrowRight className="w-5 h-5" />}
            iconPosition="right"
            className="justify-center"
          >
            Continue to Boost Packages
          </Button>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default BoostPackageSelection;